import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Sizes from 'UI/sizes';

class Button extends React.Component
{
    static propTypes = {
        size: PropTypes.oneOf([Sizes.NONE, Sizes.EXTRA_SMALL, Sizes.SMALL, Sizes.MEDIUM, Sizes.LARGE, Sizes.EXTRA_LARGE]),
        type: PropTypes.oneOf(['primary', 'secondary', 'danger', 'plain', 'blank']),
        busy: PropTypes.bool,
        disabled: PropTypes.bool,
        active: PropTypes.bool,
        grouped: PropTypes.bool,
        component: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
        onClick: PropTypes.func,
        forwardedRef: PropTypes.any,
    };

    static defaultProps = {
        size: Sizes.MEDIUM,
        type: 'plain',
        busy: false,
        disabled: false,
        active: false,
        grouped: false,
        component: 'button',
        onClick: () => {}
    };

    onClickWrapper = (e) => {
        if (this.props.disabled || this.props.busy) {
            e.preventDefault();

            return;
        }

        return this.props.onClick(e);
    };

    render()
    {
        const {
            component: Component,
            onClick,
            className,
            children,
            active,
            grouped,
            type,
            size,
            busy,
            disabled,
            forwardedRef,
            ...other
        } = this.props;

        const classes = classNames(
            className,
            'inline-flex border border-transparent items-center font-medium focus:outline-none transition ease-in-out duration-150',
            {
                'relative': busy,
                'cursor-not-allowed': disabled || busy,
                'focus:ring-1 ring-indigo-500': ! disabled,
                'rounded rounded-md': ! grouped,
                'shadow-sm': ! grouped && type !== 'blank',
            },
            {
                // blank
                'text-gray-700 outline-non': type === 'blank',
                'hover:text-gray-500 active:text-gray-800': type === 'blank' && ! disabled,
                'bg-indigo-100 border-indigo-200': active,
            },
            {
                // plain
                'border-gray-300 text-gray-700 bg-white': type === 'plain',
                'hover:text-gray-500 focus:border-indigo-300 active:text-gray-800 active:bg-gray-50': type === 'plain' && ! disabled,
                'bg-indigo-100 border-indigo-200': active,
            },
            {
                // primary
                'text-white bg-indigo-600': type === 'primary',
                'hover:bg-indigo-500 focus:border-indigo-700 active:bg-indigo-700': type === 'primary' && ! disabled,
            },
            {
                // secondary
                'text-indigo-700 bg-indigo-100': type === 'secondary',
                'hover:bg-indigo-50 focus:border-indigo-300 active:bg-indigo-200': type === 'secondary' && ! disabled,
            },
            {
                // danger
                'text-white bg-red-600': type === 'danger',
                'hover:bg-red-500 focus:border-red-700 active:bg-red-700': type === 'danger' && ! disabled,
            },
            {
                // sizes
                'px-2.5 py-1.5 text-xs leading-4': size === Sizes.EXTRA_SMALL,
                'px-3 py-2 text-sm leading-4': size === Sizes.SMALL,
                'px-4 py-2 text-sm leading-5': size === Sizes.MEDIUM,
                'px-4 py-2 text-base leading-6': size === Sizes.LARGE,
                'px-6 py-3 text-base leading-6': size === Sizes.EXTRA_LARGE,
            }
        );

        // @todo exclude bools?

        return <Component ref={forwardedRef} onClick={this.onClickWrapper} className={classes} active={active.toString()} {...other}>
            { busy && <Spinner type={type} size={size} /> }
            { children }
        </Component>;
    }
}

const Spinner = ({type, size}) => {

    const classes = classNames(
        "animate-spin box-border -ml-1 mr-2 text-white",
        {
            "text-gray-400": type === 'plain',
            "text-indigo-600": type === 'secondary',

            "w-4 h-4": size === Sizes.EXTRA_SMALL,
            "w-5 h-5": size === Sizes.SMALL || size === Sizes.MEDIUM,
            "w-6 h-6": size === Sizes.LARGE || size === Sizes.EXTRA_LARGE,
        }
    );

    return <svg className={classes} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>;
}

export default React.forwardRef((props, ref) => {
    return <Button {...props} forwardedRef={ref} />;
})